// FILE: threadDirectoryReactQuery.ts
// Purpose: react-query wiring for the thread directory (`orchestration.listThreadDirectory`).
// Layer: Client data-fetching support
// Exports: threadDirectoryQueryKeys, threadDirectoryQueryOptions, invalidateThreadDirectoryQueries

import type { ThreadDirectoryInput } from "@taskin-code/contracts";
import { queryOptions, type QueryClient } from "@tanstack/react-query";

import { ensureNativeApi } from "../nativeApi";

/**
 * How long a page stays fresh.
 *
 * The directory is a search over the local database, not a call out to a third party, so it is
 * cheap — but it is also not a live view, and re-running the full-text query on every focus flicker
 * would be a query per tab per glance for rows that change every few minutes at most.
 */
const THREAD_DIRECTORY_STALE_TIME_MS = 30_000;

export const threadDirectoryQueryKeys = {
  all: ["thread-directory"] as const,
  list: (input: ThreadDirectoryInput) => ["thread-directory", "list", input] as const,
};

/** Forgets every page, across every filter combination. */
export function invalidateThreadDirectoryQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: threadDirectoryQueryKeys.all });
}

export function threadDirectoryQueryOptions(input: ThreadDirectoryInput) {
  return queryOptions({
    queryKey: threadDirectoryQueryKeys.list(input),
    queryFn: async () => {
      const api = ensureNativeApi();
      return api.orchestration.listThreadDirectory(input);
    },
    staleTime: THREAD_DIRECTORY_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Deliberately no polling. Somebody watching this screen is looking for a thread, not watching
    // a dashboard, and the refresh button is right there.
  });
}
